import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  Divider,
  Link,
  Paper,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Routes } from '../../util/constants';
import {
  getArticleById,
  getArticlesByTopic,
  getTopicById,
  helpArticles,
  type HelpArticle as HelpArticleContent,
} from './content';

const RELATED_LIMIT = 3;

function getRelatedArticles(article: HelpArticleContent): HelpArticleContent[] {
  const sameTopic = getArticlesByTopic(article.topicId).filter(
    (related) => related.id !== article.id,
  );
  if (sameTopic.length >= RELATED_LIMIT) {
    return sameTopic.slice(0, RELATED_LIMIT);
  }

  const otherTopic = helpArticles.filter(
    (candidate) =>
      candidate.id !== article.id &&
      candidate.topicId !== article.topicId &&
      !sameTopic.some((related) => related.id === candidate.id),
  );

  return [...sameTopic, ...otherTopic].slice(0, RELATED_LIMIT);
}

export default function HelpArticle() {
  const navigate = useNavigate();
  const { articleId } = useParams<{ articleId: string }>();
  const { t } = useTranslation([], {
    keyPrefix: 'team.Components.HelpPage',
  });

  const article = articleId ? getArticleById(articleId) : undefined;

  const goToHub = () => navigate(Routes.HOME_HELP);
  const openArticle = (id: string) => navigate(`${Routes.HOME_HELP}/${id}`);

  if (!article) {
    return (
      <Box className="flex flex-col flex-1 mx-10 my-12 md:mx-20">
        <Typography
          variant="h5"
          color="primary"
          sx={{ fontWeight: 600, mb: 1 }}
        >
          {t('Article not found')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t('Article not found description')}
        </Typography>
        <Box>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={goToHub}
          >
            {t('Back to Help')}
          </Button>
        </Box>
      </Box>
    );
  }

  const topic = getTopicById(article.topicId);
  const relatedArticles = getRelatedArticles(article);

  return (
    <Box className="flex flex-col flex-1 mx-10 my-12 md:mx-20">
      <Link
        component="button"
        underline="hover"
        onClick={goToHub}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          mb: 2,
          fontSize: 14,
          alignSelf: 'flex-start',
        }}
      >
        <ArrowBackIcon fontSize="small" />
        {t('Back to Help')}
      </Link>

      {topic && (
        <Chip
          label={topic.title}
          color="primary"
          variant="outlined"
          size="small"
          sx={{ alignSelf: 'flex-start', mb: 2 }}
        />
      )}

      <Typography variant="h4" color="primary" sx={{ fontWeight: 600, mb: 1 }}>
        {article.title}
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 3 }}>
        {article.summary}
      </Typography>

      <Box sx={{ maxWidth: 760 }}>
        {article.screenshot && (
          <Box
            component="img"
            src={article.screenshot}
            alt={article.screenshotAlt ?? article.title}
            sx={{
              width: '100%',
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              mb: 3,
              display: 'block',
            }}
          />
        )}

        {article.body.map((paragraph, index) => (
          <Typography
            key={index}
            variant="body1"
            sx={{ mb: 2, lineHeight: 1.7 }}
          >
            {paragraph}
          </Typography>
        ))}
      </Box>

      {relatedArticles.length > 0 && (
        <Box sx={{ mt: 4, maxWidth: 760 }}>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            {t('Related articles')}
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(3, 1fr)',
              },
              gap: 2,
            }}
          >
            {relatedArticles.map((related) => (
              <Paper
                key={related.id}
                variant="outlined"
                onClick={() => openArticle(related.id)}
                sx={{
                  borderRadius: 2,
                  overflow: 'hidden',
                  cursor: 'pointer',
                  '&:hover': {
                    boxShadow: 3,
                    borderColor: 'primary.main',
                  },
                }}
              >
                {related.screenshot && (
                  <Box
                    component="img"
                    src={related.screenshot}
                    alt={related.screenshotAlt ?? related.title}
                    sx={{
                      width: '100%',
                      height: 110,
                      objectFit: 'cover',
                      objectPosition: 'top left',
                      display: 'block',
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                    }}
                  />
                )}
                <Box sx={{ p: 1.5 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'space-between',
                      gap: 1,
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      color="primary"
                      sx={{ fontWeight: 600 }}
                    >
                      {related.title}
                    </Typography>
                    <ArrowForwardIcon color="action" fontSize="small" />
                  </Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      mt: 0.5,
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {related.summary}
                  </Typography>
                </Box>
              </Paper>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
}
